import ArrowLeftIcon from '@mui/icons-material/ArrowLeft'
import ArrowRightIcon from '@mui/icons-material/ArrowRight'
import { Box, IconButton, Stack, Typography, useTheme } from '@mui/material'
import React from 'react'

import ToothIcon from '@/assets/images/fi-rs-tooth.png'
import Chip from '@/components/Chip'
import PieChart from '@/components/PieChart'
import Tooltip from '@/components/Tooltip'
import { useBreakpoints } from '@/hooks/useBreakpoints'

type DentinEntity = {
  dentinlayer_advice: any
  brand: any
  dentinCervicalIndex: number
  setDentinCervicalIndex: React.Dispatch<React.SetStateAction<number>>
  dentinMiddleIndex: number
  setDentinMiddleIndex: React.Dispatch<React.SetStateAction<number>>
  dentinIncisalIndex: number
  setDentinIncisalIndex: React.Dispatch<React.SetStateAction<number>>
  recipeType: any
  recipeOutput: any
}

const DentinSection = ({
  title,
  maxIndex,
  index,
  setIndex,
  layerData,
}: {
  title: string
  icon: string
  maxIndex: number
  index: number
  setIndex: React.Dispatch<React.SetStateAction<number>>
  layerData: any
}) => {
  return (
    <Box
      sx={{
        minHeight: 350,
        height: '100%',
        flexGrow: 1,
        // minWidth: '225px',
        maxWidth: '300px',
        width: '31%',
        borderRadius: '16px',
        padding: 3,
        background: `linear-gradient(
          133.84deg,
          #4e4e4e -16.04%,
          #333333 9.33%,
          #1a1a1a 32.02%,
          #1a1a1a 62.06%,
          #262626 87.42%,
          #4e4e4e 112.12%
        )`,
        '@media (max-width: 1094px)': { minWidth: '350px' },
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <img src={ToothIcon} alt="error" />
          <Typography
            fontSize={'24px'}
            fontWeight={'bolder'}
            lineHeight={'20px'}
          >
            {title}
          </Typography>
          {maxIndex > 1 && (
            <Tooltip text="Scroll through alternative options of ceramic recipe recommendations..." />
          )}
        </Box>
        <Box
          display="flex"
          alignItems="center"
          bgcolor={'#33393E'}
          borderRadius={'16px'}
        >
          <IconButton
            disabled={index === 0}
            onClick={() => setIndex((old) => old - 1)}
            sx={{ p: 0 }}
          >
            <ArrowLeftIcon sx={{ fontSize: '24px' }} />
          </IconButton>
          <Typography fontSize={'14px'} fontWeight={500} lineHeight={'20px'}>
            {index + 1}
          </Typography>
          <IconButton
            disabled={index === maxIndex - 1}
            onClick={() => setIndex((old) => old + 1)}
            sx={{ p: 0 }}
          >
            <ArrowRightIcon sx={{ fontSize: '24px' }} />
          </IconButton>
        </Box>
      </Box>

      <Box display="flex" alignItems="flex-start">
        {['recipe_layer1', 'recipe_layer2'].map((layer, i) => {
          const layerDataItem = layerData?.[layer]?.[index]
          return (
            layerDataItem && (
              <Box
                key={i}
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  textAlign: 'center',
                }}
              >
                <Box
                  sx={{
                    minHeight: '48px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant="body1" fontWeight="bold">
                    {layerDataItem.opacity_category}
                  </Typography>
                </Box>

                <PieChart
                  labels={layerDataItem.powders}
                  values={layerDataItem.simplified_ratios}
                  height={'100px'}
                  width={'100px'}
                />
              </Box>
            )
          )
        })}
      </Box>
    </Box>
  )
}

const Dentin = ({
  dentinlayer_advice,
  brand,
  dentinCervicalIndex,
  setDentinCervicalIndex,
  dentinMiddleIndex,
  setDentinMiddleIndex,
  dentinIncisalIndex,
  setDentinIncisalIndex,
  recipeOutput,
}: DentinEntity) => {
  const theme = useTheme()

  const dentinSections = [
    {
      key: 'cervical',
      title: 'Cervical',
      maxIndex: Math.max(
        dentinlayer_advice?.dentinrecipes_cervical?.recipe_layer1?.length || 0,
        dentinlayer_advice?.dentinrecipes_cervical?.recipe_layer2?.length || 0,
      ),
      index: dentinCervicalIndex,
      setIndex: setDentinCervicalIndex,
      layerData: dentinlayer_advice?.dentinrecipes_cervical,
    },
    {
      key: 'middle',
      title: 'Middle',
      maxIndex: Math.max(
        dentinlayer_advice?.dentinrecipes_middle?.recipe_layer1?.length || 0,
        dentinlayer_advice?.dentinrecipes_middle?.recipe_layer2?.length || 0,
      ),
      index: dentinMiddleIndex,
      setIndex: setDentinMiddleIndex,
      layerData: dentinlayer_advice?.dentinrecipes_middle,
    },
    {
      key: 'incisal',
      title: 'Incisal',
      maxIndex: Math.max(
        dentinlayer_advice?.dentinrecipes_incisal?.recipe_layer1?.length || 0,
        dentinlayer_advice?.dentinrecipes_incisal?.recipe_layer2?.length || 0,
      ),
      index: dentinIncisalIndex,
      setIndex: setDentinIncisalIndex,
      layerData: dentinlayer_advice?.dentinrecipes_incisal,
    },
  ]

  const isSingleDentin =
    recipeOutput?.provided_input?.recipe_type === 'single dentin'

  const updatedList = dentinSections?.filter((item) =>
    isSingleDentin ? item?.key === 'middle' : item,
  )

  const { isDisplay1300 } = useBreakpoints()

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', height: '100%', px: 4 }}
    >
      <Box display="flex" alignItems="center" pb={2} gap={1}>
        <Typography
          sx={{ fontSize: '1rem', fontWeight: 400, lineHeight: '20px' }}
        >
          Dentin:
        </Typography>
        <Chip
          label={brand}
          sx={{
            bgcolor: theme.palette.background.chip,
            color: theme.palette.common.black,
            '&:hover': { bgcolor: theme.palette.background.chip },
          }}
          size="small"
        />
      </Box>

      <Stack
        direction="row"
        spacing={5}
        width="100%"
        sx={{
          justifyContent: 'center',
          ...(isDisplay1300
            ? { gap: '0', flexWrap: 'nowrap' }
            : { gap: '32px', flexWrap: 'wrap' }),
        }}
      >
        {updatedList?.map(
          ({ key, title, maxIndex, index, setIndex, layerData }) => (
            <>
              <DentinSection
                title={title}
                icon={ToothIcon}
                maxIndex={maxIndex}
                index={index}
                setIndex={setIndex}
                layerData={layerData}
                key={key}
              />
            </>
          ),
        )}
      </Stack>
    </Box>
  )
}

export default Dentin
